import { useEffect } from "react"
import { Link } from "react-router-dom"
import { Disclosure } from "@headlessui/react"
import {
    ArrowUpRightIcon,
    BuildingOffice2Icon,
    CalendarIcon,
    ChevronDownIcon,
    QuestionMarkCircleIcon,
    UserGroupIcon,
} from "@heroicons/react/20/solid";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const linkClass = "font-medium text-indigo-600 hover:text-indigo-500";

function Steps({ children }) {
    return <ol className="list-decimal space-y-1 pl-5">{children}</ol>
}

function Sub({ children }) {
    return <ul className="mt-1 list-disc space-y-0.5 pl-5">{children}</ul>
}

function Heading({ children }) {
    return <p className="mb-1 font-medium text-gray-900">{children}</p>
}

function Note({ label, children }) {
    return (
        <p className="mt-3 text-gray-500">
            <span className="font-medium text-gray-700">{label}</span> {children}
        </p>
    )
}

const actions = [
    {
        title: 'Gebruikers',
        description: 'Beheer de gebruikers van Assemblo.',
        href: 'users',
        icon: UserGroupIcon,
        iconForeground: 'text-red-700',
        iconBackground: 'bg-red-50',
    },
    {
        title: 'Gebouwen',
        description: 'Beheer de gebouwen van Assemblo.',
        href: 'buildings',
        icon: BuildingOffice2Icon,
        iconForeground: 'text-purple-700',
        iconBackground: 'bg-purple-50',
    },
    {
        title: 'Meetings',
        description: 'Beheer de meetings van Assemblo.',
        href: 'meetings',
        icon: CalendarIcon,
        iconForeground: 'text-blue-700',
        iconBackground: 'bg-blue-50',
    },
    {
        title: 'Hulp',
        description: 'Vragen of problemen? Bel ons.',
        href: '+32472714637',
        tel: true,
        icon: QuestionMarkCircleIcon,
        iconForeground: 'text-yellow-700',
        iconBackground: 'bg-yellow-50',
    },
]

const faqs = [
    {
        question: 'Hoe exporteer ik de stemresultaten van een meeting?',
        answer: (
            <div>
                <Steps>
                    <li>Ga naar <Link to="/admin/meetings" className={linkClass}>Meetings</Link>.</li>
                    <li>Zoek de meeting in de tabel en klik op het tandwiel-icoon om naar de instellingen te gaan.</li>
                    <li>Scroll naar de exportsectie en kies wat je wilt exporteren:
                        <Sub>
                            <li>Chatgeschiedenis: alle chatberichten van de meeting.</li>
                            <li>Stemresultaten: alle stemmen, inclusief gebruikersgegevens.</li>
                            <li>Aanwezigheden: wie de meeting heeft bijgewoond.</li>
                            <li>Reviews: feedback van deelnemers.</li>
                        </Sub>
                    </li>
                    <li>Klik op de gewenste exportknop. Het bestand wordt als Excel (.xlsx) gedownload.</li>
                </Steps>
                <Note label="Goed om te weten:">de export bevat tijdstempels en gebruikersinformatie.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe exporteer ik alle data uit het systeem?',
        answer: (
            <div>
                <Steps>
                    <li>Ga naar <Link to="/admin/administrator" className={linkClass}>Administrator</Link>.</li>
                    <li>Klik in de sectie "Data exporteren" op de knop "Data exporteren".</li>
                    <li>Voer het super admin wachtwoord in en klik op "Doorgaan".</li>
                    <li>Het systeem genereert een Excel-bestand met twaalf werkbladen: gebruikers, meetings, gebouwen, aanwezigheden, stemmen, vragen, chats, reviews, contracten, signages, wachtschermen en televisiecontent.</li>
                </Steps>
                <Note label="Privacy:">PIN-codes worden niet geëxporteerd, enkel de gehashte versie.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe maak ik een nieuwe online vergadering (AV) aan?',
        answer: (
            <div>
                <Steps>
                    <li>Ga naar <Link to="/admin/meetings" className={linkClass}>Meetings</Link>.</li>
                    <li>Klik rechts bovenaan op "Nieuwe AV aanmaken".</li>
                    <li>Vul de naam, datum, tijd en het gebouw in.</li>
                    <li>Klik op "Aanmaken".</li>
                </Steps>
                <Note label="Let op:">het gebouw moet al bestaan voor je een meeting aanmaakt. Datum en tijd kun je achteraf nog aanpassen via het tandwiel-icoon bij de meeting.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe voeg ik nieuwe gebruikers toe?',
        answer: (
            <div className="space-y-4">
                <div>
                    <Heading>Eén gebruiker</Heading>
                    <Steps>
                        <li>Ga naar <Link to="/admin/users" className={linkClass}>Gebruikers</Link>.</li>
                        <li>Klik rechts bovenaan op "Nieuwe gebruiker toevoegen".</li>
                        <li>Vul voornaam, achternaam, e-mailadres en gebouw in. Adminrechten en taal zijn optioneel.</li>
                        <li>Klik op "Opslaan".</li>
                    </Steps>
                </div>
                <div>
                    <Heading>Meerdere gebruikers via Excel</Heading>
                    <Steps>
                        <li>Klik rechts bovenaan op "Excel uploaden".</li>
                        <li>Kies een Excel-bestand met de gebruikersgegevens. Het systeem importeert alle rijen.</li>
                    </Steps>
                </div>
                <Note label="Inloggen:">nieuwe gebruikers ontvangen een magic link per e-mail. Er zijn geen wachtwoorden. Gebruik de zoekbalk boven de tabel om iemand snel terug te vinden.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe stel ik een PIN-code in voor een administrator?',
        answer: (
            <div>
                <p className="mb-2">PIN-codes dienen om snel in te loggen op een tablet tijdens een meeting.</p>
                <Steps>
                    <li>Ga naar <Link to="/admin/pins" className={linkClass}>PIN beheer</Link>.</li>
                    <li>Klik op "PIN instellen".</li>
                    <li>Voer het super admin wachtwoord in. Dit is niet je gewone login.</li>
                    <li>Kies de administrator in de lijst en voer een PIN van zes cijfers in.</li>
                    <li>Klik op "PIN instellen".</li>
                </Steps>
                <Note label="Zwakke PIN's worden geweigerd:">veelgebruikte codes zoals 123456 of 000000, opeenvolgende cijfers en zes dezelfde cijfers.</Note>
                <Note label="Goed om te weten:">PIN's kunnen enkel voor admins ingesteld worden en worden gehasht opgeslagen. De originele PIN is achteraf niet meer zichtbaar.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe maak ik een nieuw gebouw aan?',
        answer: (
            <div>
                <Steps>
                    <li>Ga naar <Link to="/admin/buildings" className={linkClass}>Gebouwen</Link>.</li>
                    <li>Klik rechts bovenaan op "Nieuw gebouw toevoegen".</li>
                    <li>Upload een afbeelding en vul de naam van het gebouw in.</li>
                    <li>Kies het type:
                        <Sub>
                            <li>BV: besloten vennootschap. Vul de proxystemmen in (NL en FR).</li>
                            <li>VME: vereniging van mede-eigenaars. Vul de proxy- en onlinestemmen in (NL en FR).</li>
                            <li>IV: infovergadering. Geen extra velden.</li>
                        </Sub>
                    </li>
                    <li>Klik op "Opslaan".</li>
                </Steps>
                <Note label="Volgorde:">maak eerst het gebouw aan. Meetings en gebruikers zijn altijd aan een gebouw gekoppeld.</Note>
                <Note label="DocuSign:">contracten moeten meteen bij het aanmaken van een AV gekoppeld worden en kunnen enkel door Louis Mylle klaargezet worden.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe stel ik het wachtscherm in?',
        answer: (
            <div>
                <p className="mb-2">Het wachtscherm zien deelnemers zolang de meeting nog niet gestart is. Naast de aftelling toont het tot twee projectkaarten.</p>
                <Steps>
                    <li>Ga naar <Link to="/admin/waiting-screen" className={linkClass}>Wachtscherm</Link>.</li>
                    <li>Zet bij elke kaart die je wilt tonen de schakelaar "Tonen" aan.</li>
                    <li>Upload per kaart een afbeelding en vul per taal (NL, FR en EN) de pretitel, titel, subtitel en knoptekst in. De subtitel ondersteunt opmaak.</li>
                    <li>Vul per kaart de link in waar de knop naartoe gaat en klik op "Opslaan".</li>
                </Steps>
                <Note label="Voorbeeld:">onderaan de pagina zie je de wachtpagina zoals deelnemers ze te zien krijgen, per taal. Kaarten die uit staan, worden niet getoond.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe pas ik de login e-mail aan?',
        answer: (
            <div>
                <p className="mb-2">Dit is de e-mail met de aanmeldlink die gebruikers krijgen wanneer ze inloggen.</p>
                <Steps>
                    <li>Ga naar <Link to="/admin/login-email" className={linkClass}>Login e-mail</Link>.</li>
                    <li>Pas het onderwerp, de HTML-versie en de tekstversie aan. Zet {'{{link}}'} op de plek waar de aanmeldlink moet komen, in beide versies.</li>
                    <li>Klik op "Testmail naar mezelf" om het resultaat in je eigen mailbox te bekijken. De link in een testmail werkt niet.</li>
                    <li>Klik op "Opslaan". Vanaf dan krijgt iedereen die inlogt de nieuwe versie.</li>
                </Steps>
                <Note label="Terug naar het origineel:">met "Standaardtekst terugzetten" laad je de oorspronkelijke tekst opnieuw in. Die wordt pas gebruikt nadat je opslaat.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe verwijder ik oude meetings en data?',
        answer: (
            <div>
                <p className="mb-2 font-medium text-gray-900">Dit kan niet ongedaan gemaakt worden. Maak eerst een volledige export als back-up.</p>
                <Steps>
                    <li>Ga naar <Link to="/admin/administrator" className={linkClass}>Administrator</Link>.</li>
                    <li>Kies in de sectie "Systeem opschonen" wat je wilt verwijderen:
                        <Sub>
                            <li>Schakelaar uit: enkel afgelopen meetings.</li>
                            <li>Schakelaar aan: alle meetings, ook toekomstige.</li>
                        </Sub>
                    </li>
                    <li>Klik op "Systeem opschonen", voer het super admin wachtwoord in en bevestig.</li>
                    <li>Het systeem toont hoeveel items verwijderd zijn.</li>
                </Steps>
                <Note label="Wordt verwijderd:">de gekozen meetings met al hun stemmen, chats, vragen, aanwezigheden, reviews en signages, plus gewone gebruikers die aan geen enkele meeting meer gekoppeld zijn.</Note>
                <Note label="Blijft behouden:">administrators, medewerkers met een @upgrade-estate.be adres, gebouwen en contracten.</Note>
            </div>
        ),
    },
    {
        question: 'Waar vind ik de audit logs?',
        answer: (
            <div>
                <p className="mb-2">De audit logs registreren alle gevoelige acties.</p>
                <Steps>
                    <li>Ga naar <Link to="/admin/audit" className={linkClass}>Audit logs</Link>.</li>
                    <li>Filter op operatietype, op succes of fout, of op gebruiker.</li>
                    <li>Klik op een regel voor de details: tijdstip, gebruiker, IP-adres, operatie, status en de inhoud van de actie.</li>
                </Steps>
                <Note label="Wordt gelogd:">systeem opschonen (met het aantal verwijderde items), volledige exports, PIN's instellen, IP-blokkades en deblokkades via Fail2Ban, en elke verificatie van het super admin wachtwoord.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe start ik een meeting en beheer ik live stemmingen?',
        answer: (
            <div>
                <Steps>
                    <li>Ga naar <Link to="/admin/meetings" className={linkClass}>Meetings</Link> en klik bij de meeting op het afspeel-icoon.</li>
                    <li>Je komt op het meetingdashboard met de statistieken (kijkers, chatberichten, stemmen), de live chat en de stemmingen.</li>
                    <li>Om een stemming te starten vul je de vraag in bij "Nieuwe stemming" en klik je op "Start stemming". Deelnemers stemmen ja, nee of onthouding en je ziet de resultaten live binnenkomen.</li>
                    <li>Klik op "Stop stemming" om de stemming af te sluiten.</li>
                </Steps>
                <Note label="Na de meeting:">alle stemresultaten blijven beschikbaar en kun je exporteren via de instellingen van de meeting.</Note>
            </div>
        ),
    },
    {
        question: 'Hoe wijzig of verwijder ik een gebruiker?',
        answer: (
            <div className="space-y-4">
                <div>
                    <Heading>E-mailadres wijzigen</Heading>
                    <Steps>
                        <li>Ga naar <Link to="/admin/users" className={linkClass}>Gebruikers</Link> en zoek de gebruiker via de zoekbalk.</li>
                        <li>Klik op het e-mailadres in de tabel. Het wordt een invoerveld.</li>
                        <li>Typ het nieuwe adres en druk op Enter of klik buiten het veld. De wijziging wordt meteen opgeslagen.</li>
                    </Steps>
                </div>
                <div>
                    <Heading>Gebruiker verwijderen</Heading>
                    <Steps>
                        <li>Zoek de gebruiker in de tabel.</li>
                        <li>Klik rechts in de rij op "Verwijderen" en bevestig.</li>
                    </Steps>
                </div>
                <Note label="Let op:">na een adreswijziging logt de gebruiker voortaan in met het nieuwe e-mailadres. Met de tabs boven de tabel (Gebruikers, Medewerkers, Admins, Alles) filter je per groep.</Note>
            </div>
        ),
    },
    {
        question: 'Wat is het super admin wachtwoord?',
        answer: (
            <div>
                <p>Een extra beveiligingslaag voor gevoelige operaties. Het wordt gevraagd bij:</p>
                <Sub>
                    <li>een volledige data-export;</li>
                    <li>het opschonen van het systeem;</li>
                    <li>het instellen van PIN-codes;</li>
                    <li>het deblokkeren van IP-adressen in Fail2Ban.</li>
                </Sub>
                <p className="mt-3">Het staat los van je logingegevens. Admins loggen in met e-mail en magic link, of met een PIN-code.</p>
                <Note label="Wachtwoord vergeten?">Neem contact op met Louis Mylle via <a href="tel:+32472714637" className={linkClass}>+32 472 71 46 37</a> of <a href="mailto:louis@upgrade-estate.be" className={linkClass}>louis@upgrade-estate.be</a>.</Note>
            </div>
        ),
    },
]

const troubleshooting = [
    {
        question: 'Een gebruiker is geblokkeerd en kan niet meer inloggen',
        answer: (
            <div>
                <p className="mb-2">Fail2Ban blokkeert automatisch een IP-adres na te veel mislukte inlogpogingen: drie foute PIN's (60 minuten) of vijf foute e-maillogins (30 minuten). Het IP-adres van een admin komt na een geslaagde login twaalf uur op de whitelist.</p>
                <Steps>
                    <li>Ga naar <Link to="/admin/fail2ban" className={linkClass}>Fail2Ban</Link>.</li>
                    <li>Zoek het IP-adres in de lijst. Je ziet de reden, wanneer de blokkade vervalt en welke PIN's of wachtwoorden geprobeerd zijn.</li>
                    <li>Klik op "Deblokkeer".</li>
                </Steps>
            </div>
        ),
    },
]

function FaqList({ items }) {
    return (
        <dl className="divide-y divide-gray-200 rounded-lg bg-white shadow">
            {items.map((item) => (
                <Disclosure as="div" key={item.question} className="px-6 py-5">
                    {({ open }) => (
                        <>
                            <dt>
                                <Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-900">
                                    <span className="text-sm font-medium leading-6">{item.question}</span>
                                    <span className="ml-6 flex h-6 items-center">
                                        <ChevronDownIcon
                                            className={classNames(open ? '-rotate-180' : '', 'h-5 w-5 text-gray-400 transition-transform')}
                                            aria-hidden="true"
                                        />
                                    </span>
                                </Disclosure.Button>
                            </dt>
                            <Disclosure.Panel as="dd" className="mt-3 pr-12 text-sm leading-6 text-gray-600">
                                {item.answer}
                            </Disclosure.Panel>
                        </>
                    )}
                </Disclosure>
            ))}
        </dl>
    )
}

function FaqSection({ title, description, items }) {
    return (
        <section className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div className="lg:col-span-1">
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                <p className="mt-1 text-sm text-gray-500">{description}</p>
            </div>
            <div className="mt-4 lg:col-span-2 lg:mt-0">
                <FaqList items={items} />
            </div>
        </section>
    )
}

export default function AdminHomeView() {
    useEffect(() => {
        document.title = "Assemblo - Admin - Dashboard"
    });

    return (
        <main className="flex-1">
            <div className="py-6">
                <div className="mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Klik door naar de verschillende onderdelen binnen Assemblo.
                    </p>
                </div>

                <div className="mx-auto px-4 sm:px-6 md:px-8 mt-8 space-y-12">
                    <div className="grid grid-cols-1 gap-px overflow-hidden rounded-lg bg-gray-200 shadow sm:grid-cols-2 lg:grid-cols-4">
                        {actions.map((action) => (
                            <div
                                key={action.title}
                                className="group relative bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500"
                            >
                                <span
                                    className={classNames(
                                        action.iconBackground,
                                        action.iconForeground,
                                        'inline-flex rounded-lg p-3'
                                    )}
                                >
                                    <action.icon className="h-6 w-6" aria-hidden="true" />
                                </span>
                                <div className="mt-6">
                                    <h3 className="text-base font-semibold text-gray-900">
                                        {action.tel ? (
                                            <a href={`tel:${action.href}`} className="focus:outline-none">
                                                <span className="absolute inset-0" aria-hidden="true" />
                                                {action.title}
                                            </a>
                                        ) : (
                                            <Link to={action.href} className="focus:outline-none">
                                                <span className="absolute inset-0" aria-hidden="true" />
                                                {action.title}
                                            </Link>
                                        )}
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500">{action.description}</p>
                                </div>
                                <span
                                    className="pointer-events-none absolute right-6 top-6 text-gray-300 group-hover:text-gray-400"
                                    aria-hidden="true"
                                >
                                    <ArrowUpRightIcon className="h-5 w-5" />
                                </span>
                            </div>
                        ))}
                    </div>

                    <FaqSection
                        title="Veelgestelde vragen"
                        description="Stapsgewijze uitleg bij de meest voorkomende taken."
                        items={faqs}
                    />

                    <FaqSection
                        title="Problemen oplossen"
                        description="Wat te doen als iets niet werkt zoals verwacht."
                        items={troubleshooting}
                    />
                </div>
            </div>
        </main>
    )
}
